import { Intro, StatStrip, Friction, HeroStat, CategoryGrid, Callout, Takeaway, SectionLead, Disclaimer, Shot } from './components.jsx';

export const meta = {
  slug: 'bricks-and-figs-de',
  metaTitle: 'How BricksAndFigsDE Generated €222K Shopify Sales | Account Editor',
  metaDescription:
    'See how BricksAndFigsDE unlocked roughly €222K in incremental revenue by letting customers add items, upgrade shipping, and edit orders after checkout.',
  brand: 'BricksAndFigsDE',
  industry: 'Custom LEGO Collectibles · DTC',
  statNum: '~€222K',
  statDesc: 'Added-order revenue · 4,479 post-purchase edits',
  detail:
    'How a collector-focused LEGO store unlocked roughly €222K in incremental revenue by letting customers add items, upgrade shipping, and edit orders after checkout — without paying for a second shipment.',
  quote:
    '"Our collectors never stop at one. Before, wanting another figure meant a second order and a second shipping fee — so a lot of them just didn\'t. Now they add it to the order they\'ve already placed in a couple of taps, and our average order has grown right alongside it."',
  author: 'The team at BricksAndFigsDE',
  role: 'Custom LEGO Star Wars Minifigures · Germany',
  img: 'BricksAndFigsDE',
  hero: 'BricksAndFigsDE+Case+Study',
  image: '/images/case-studies/bricks-and-figs-de-case-study.webp',
  readTime: '5 min read',
  date: 'May 25, 2026'
};

export default function Content() {
  return (
    <>
      <Intro>
        One more figure for the order — without a second order. BricksAndFigsDE can't merge two
        Shopify orders, so when a collector decided they wanted another figure five minutes after
        checking out, the choice was a fresh order plus a second shipping fee, or nothing at all.
      </Intro>

      <StatStrip
        items={[
          { num: '~€222K', label: 'est. added-order revenue', highlight: true },
          { num: '3,000', label: 'paid items added to existing orders' },
          { num: '4,479', label: 'total post-purchase edits' },
          { num: '1,080', label: 'orders touched by the feature' },
        ]}
        period="Jan 2025 – May 2026 · 16 months"
      />

      <SectionLead eyebrow="About the merchant" title="A collector's store, where one figure is rarely enough.">
        BricksAndFigsDE is run by Niki Ataei — limited drops of custom LEGO Star Wars
        minifigures, ~€60 per figure, a 54,000-strong audience of repeat-buying collectors. The
        post-purchase moment is not a service problem here. It is a buying signal.
      </SectionLead>

      <SectionLead eyebrow="The friction" title="The constraint that quietly bled revenue.">
        Shopify does not allow merging two separate orders. When a collector wanted to add a figure
        right after checking out, they had two bad options.
      </SectionLead>

      <Friction
        question="I just saw another figure — I want to add it to the order I already placed."
        oldWay="Place a brand-new order, pay shipping again, wait for a second package. Most collectors just skip it."
        newWay="Add the figure directly to the existing order in a couple of taps — same shipment, no second checkout."
        callout="Customers added items <strong>2,740</strong> times and increased quantities on another <strong>278</strong> orders."
      />

      <SectionLead eyebrow="The add-to-order engine" title="Turning 'I'll get it next time' into action.">
        Account Editor put add-item, quantity, swap, shipping-upgrade and address-fix controls on the
        customer's own order page — before the order leaves the warehouse.
      </SectionLead>

      <HeroStat
        num="~€222K"
        label="estimated added-order revenue"
        period="Jan 2025 – May 2026"
        breakdown={[
          { label: 'Paid actions', num: '3,000', note: 'add-item + increase-quantity' },
          { label: 'Shipping upgrades', num: '681', note: 'paid mid-order upgrades' },
        ]}
      />

      <Shot
        src="/images/case-studies/bricks-and-figs-add-to-order.webp"
        alt="BricksAndFigsDE customer adding more items to an order they've already placed, from the order status page"
        caption="Customers add more to an order they've already placed — straight from the order page."
      />

      <SectionLead title="Every edit, doing its job.">
        Across eight distinct edit types, customers used Account Editor for three different jobs — all
        from the same surface.
      </SectionLead>

      <CategoryGrid
        groups={[
          {
            title: 'Grows the order',
            items: [
              { label: 'Add item', num: '2,740' },
              { label: 'Increase quantity', num: '278' },
              { label: 'Upgrade shipping', num: '681' },
            ],
          },
          {
            title: 'Saves the sale',
            items: [
              { label: 'Remove item', num: '498' },
              { label: 'Decrease quantity', num: '112' },
              { label: 'Swap an item', num: '69' },
            ],
          },
          {
            title: 'Protects delivery',
            items: [
              { label: 'Fix shipping address', num: '92' },
              { label: 'Fix contact info', num: '9' },
            ],
          },
        ]}
      />

      <Callout>
        Add-to-order became the store's single most-used post-purchase action — driving AOV growth
        without a single euro of new ad spend.
      </Callout>

      <Takeaway footnote="Measured across the BricksAndFigsDE deployment · Jan 2025 – May 2026">
        ~<strong>€222K</strong> in incremental revenue. <strong>4,479</strong> edits across eight
        action types. One order page that finally matches how collectors actually buy.
      </Takeaway>

      <Disclaimer>
        Figures cover Jan 2025 – May 2026. Added-order revenue is modelled from 3,000 paid
        add-item and quantity-increase actions at an estimated ~€74 AOV proxy. Shipping upgrades
        and other action counts are measured directly from the Account Editor deployment.
      </Disclaimer>
    </>
  );
}
